"use client";

import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../cn";
import { FOCUS_RING } from "./focus-ring";

// Pressed state is the accent tint (design spec §6.2 toolbar buttons); `outline` adds the
// secondary-button hairline. Sizes follow the button scale (28 / 36 / 40).
const toggleVariants = cva(
  `group/toggle inline-flex items-center justify-center gap-1.5 rounded-md text-sm font-ui whitespace-nowrap text-secondary transition-[color,background-color,border-color] duration-(--dur-fast) hover:bg-muted hover:text-foreground ${FOCUS_RING} aria-pressed:bg-primary-soft aria-pressed:text-primary-text data-pressed:bg-primary-soft data-pressed:text-primary-text disabled:cursor-not-allowed disabled:opacity-55 aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4`,
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border border-input bg-card hover:border-strong hover:bg-sunken",
        /**
         * DTP filter chip, the base of `Segmented`. The pressed chip is cobalt (--primary-soft
         * fill, --primary hairline, --primary-text): wave 7, the white card on white read as
         * nothing chosen. The others are plain text that greys on hover.
         */
        chip: "border border-transparent text-muted-foreground hover:bg-muted hover:text-foreground aria-pressed:border-primary aria-pressed:bg-primary-soft aria-pressed:text-primary-text aria-pressed:hover:bg-primary-soft data-pressed:border-primary data-pressed:bg-primary-soft data-pressed:text-primary-text data-pressed:hover:bg-primary-soft",
      },
      size: {
        default:
          "h-9 min-w-9 px-3 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5",
        sm: "h-7 min-w-7 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-10 min-w-10 px-3.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
      },
    },
    compoundVariants: [
      { variant: "chip", size: "sm", class: "h-7 rounded-sm px-2.5 text-xs font-medium" },
      { variant: "chip", size: "default", class: "h-8 rounded-sm px-[11px] text-xs font-medium" },
      { variant: "chip", size: "lg", class: "h-9 rounded-sm px-3 text-sm font-medium" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Toggle({
  className,
  variant = "default",
  size = "default",
  ...props
}: TogglePrimitive.Props & VariantProps<typeof toggleVariants>) {
  return (
    <TogglePrimitive
      data-slot="toggle"
      className={cn(toggleVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Toggle, toggleVariants };
